import type { CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import type { PublicationListOut } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import type { FilterOption } from '@/components/common/SelectFilter';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type PublicationSelectFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
};

/**
 * `publication_id[eq]` column filter with server-side title autocomplete —
 * type a title fragment, pick a publication, filter by its id.
 */
export const PublicationSelectFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
}: PublicationSelectFilterProps) => {
  const current = getFilterValue<number>(filters, field, 'eq');

  const { selectProps } = useSelect<PublicationListOut>({
    resource: 'user_catalog.publications',
    optionLabel: 'title',
    optionValue: 'id',
    // Hydrates the option label when the filter is already set (e.g. from URL).
    defaultValue: current,
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  return (
    <SelectFilter<number>
      field={field}
      options={(selectProps.options ?? []) as FilterOption<number>[]}
      filters={filters}
      confirm={confirm}
      clearFilters={clearFilters}
      setFilters={setFilters}
      placeholder={placeholder}
      minWidth={260}
      selectProps={{
        showSearch: true,
        filterOption: false,
        onSearch: selectProps.onSearch,
      }}
    />
  );
};
